import {
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  OutlinedInput,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { AddOutlined, Search, ThumbDownOffAlt } from "@mui/icons-material";

import { useEffect, useState } from "react";
import { theme } from "../../../theme/theme";

import Swal from "sweetalert2";
import { toast } from "sonner";
import useDebounce from "../../../hooks/useDebounce";
import useDisclosure from "../../../hooks/useDisclosure";

import noRecordsFound from "../../../assets/svg/noRecordsFound.svg";
import somethingWentWrong from "../../../assets/svg/SomethingWentWrong.svg";

import UserAccountAction from "./UserAccountAction";
import UserAccountPermissions from "./UserAccountPermissions";
import UserAccountDialog from "./UserAccountDialog";
import { useGetUsersQuery, useResetUserPasswordMutation, useArchiveUserMutation, useGetPendingUsersQuery } from "../../../features/user_management_api/user/userApi";
import PendingAccountsTab from "./PendingAccounts/PendingAccountsTab";

const UserAccounts = () => {
  const [status, setStatus] = useState("true");
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(100);

  const [searchValue, setSearchValue] = useState("");
  const search = useDebounce(searchValue, 500);

  const [editData, setEditData] = useState(null);
  const [onPendingTab, setOnPendingTab] = useState(null);

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { open, onToggle, onClose } = useDisclosure();

  const { data, isLoading, isFetching, isSuccess, isError } = useGetUsersQuery({
    Status: status,
    Search: search,
    PageNumber: pageNumber,
    PageSize: pageSize,
  });

  const {
    data: pendingUsersData,
    isLoading: pendingUsersiIsLoading,
    isFetching: pendingUsersIsFetching,
    isSuccess: pendingUsersIsSuccess,
    isError: pendingUsersIsError,
  } = useGetPendingUsersQuery({
    Search: search,
    PageNumber: pageNumber,
    PageSize: pageSize,
  });

  const [resetUser] = useResetUserPasswordMutation();
  const [archiveUser] = useArchiveUserMutation();

  const onPageNumberChange = (_, page) => {
    setPageNumber(page + 1);
  };

  const onPageSizeChange = (e) => {
    setPageSize(e.target.value);
    setPageNumber(1);
  };

  const onStatusChange = (_, newValue) => {
    console.log("NewValue: ", newValue);
    if (newValue === "pending") {
      setOnPendingTab(newValue);
      setStatus(newValue);
      setPageNumber(1);
      setPageSize(5);
    } else {
      setOnPendingTab(null);
      setStatus(newValue);
      setPageNumber(1);
      setPageSize(5);
    }
  };

  const onDialogClose = () => {
    setEditData(null);
    onClose();
  };

  const onArchiveAction = (data) => {
    const userJson = sessionStorage.getItem("user");
    const user = JSON.parse(userJson);
    const userId = user.id;

    if (data.isActive === true) {
      Swal.fire({
        title: "Are you sure?",
        text: "This will move this user to the archived tab.",
        icon: "warning",
        color: "white",
        showCancelButton: true,
        background: "#111927",
        confirmButtonColor: "#9e77ed",
        cancelButtonColor: "#1C2536",
        heightAuto: false,
        width: "30em",
        allowOutsideClick: false,
        customClass: {
          container: "custom-container",
          title: "custom-title",
          htmlContainer: "custom-text",
          icon: "custom-icon",
          confirmButton: "custom-confirm-btn",
          cancelButton: "custom-cancel-btn",
        },
        preConfirm: async () => {
          try {
            Swal.getConfirmButton().innerHTML = `
                    <div class="mui-btn-content">
                      <span class="mui-spinner"></span>
                      <span class="mui-label">Archiving...</span>
                    </div>
                  `;
            Swal.getConfirmButton().disabled = true;

            if (userId === data?.id) {
              toast.error("Archive Error!", {
                description: "The logged-in user cannot be archived.",
                duration: 1500,
              });
            } else {
              await archiveUser(data)
                .unwrap()
                .then(() => {
                  toast.success("Success!", {
                    description: "Archive successfully.",
                    duration: 1500,
                  });
                })
                .catch((error) => {
                  toast.error("Error!", {
                    description: error.data.error.message,
                    duration: 1500,
                  });
                });
            }

            return true; // resolves success
          } catch (err) {
            console.log("Error", err);
            Swal.hideLoading();
            Swal.showValidationMessage(err?.data?.error?.message || "Something went wrong");
            return false; // keeps modal open
          }
        },
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.close();
        }
      });
    } else {
      Swal.fire({
        title: "Are you sure?",
        text: "This will move this user to the active tab.",
        icon: "warning",
        color: "white",
        showCancelButton: true,
        background: "#111927",
        confirmButtonColor: "#9e77ed",
        cancelButtonColor: "#1C2536",
        heightAuto: false,
        width: "30em",
        allowOutsideClick: false,
        customClass: {
          container: "custom-container",
          title: "custom-title",
          htmlContainer: "custom-text",
          icon: "custom-icon",
          confirmButton: "custom-confirm-btn",
          cancelButton: "custom-cancel-btn",
        },
        preConfirm: async () => {
          try {
            Swal.getConfirmButton().innerHTML = `
                    <div class="mui-btn-content">
                      <span class="mui-spinner"></span>
                      <span class="mui-label">Restoring...</span>
                    </div>
                  `;
            Swal.getConfirmButton().disabled = true;

            if (userId === data?.id) {
              toast.error("Archive Error!", {
                description: "The logged-in user cannot be archived.",
                duration: 1500,
              });
            } else {
              await archiveUser(data)
                .unwrap()
                .then(() => {
                  toast.success("Success!", {
                    description: "Restore successfully.",
                    duration: 1500,
                  });
                })
                .catch((error) => {
                  toast.error("Error!", {
                    description: error.data.error.message,
                    duration: 1500,
                  });
                });
            }

            return true; // resolves success
          } catch (err) {
            console.log("Error", err);
            Swal.hideLoading();
            Swal.showValidationMessage(err?.data?.error?.message || "Something went wrong");
            return false; // keeps modal open
          }
        },
      }).then((result) => {
        if (result.isConfirmed) {
          Swal.close();
        }
      });
    }
  };

  const onResetPasswordAction = (data) => {
    Swal.fire({
      title: "Are you sure?",
      text: "This will reset the password of this user.",
      icon: "warning",
      color: "white",
      showCancelButton: true,
      background: "#111927",
      confirmButtonColor: "#9e77ed",
      cancelButtonColor: "#1C2536",
      heightAuto: false,
      width: "30em",
      allowOutsideClick: false,
      customClass: {
        container: "custom-container",
        title: "custom-title",
        htmlContainer: "custom-text",
        icon: "custom-icon",
        confirmButton: "custom-confirm-btn",
        cancelButton: "custom-cancel-btn",
      },
      preConfirm: async () => {
        try {
          Swal.getConfirmButton().innerHTML = `
              <div class="mui-btn-content">
                <span class="mui-spinner"></span>
                <span class="mui-label">Resetting...</span>
              </div>
            `;
          Swal.getConfirmButton().disabled = true;
          resetUser(data)
            .unwrap()
            .then(() => {
              toast.success("Success!", {
                description: "Reset password successfully.",
                duration: 1500,
              });
            })
            .catch((error) => {
              toast.error("Error!", {
                description: error.data.error.message,
                duration: 1500,
              });
            });

          return true; // resolves success
        } catch (err) {
          console.log("Error", err);
          Swal.hideLoading();
          Swal.showValidationMessage(err?.data?.error?.message || "Something went wrong");
          return false; // keeps modal open
        }
      },
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.close(); // close once API success
      }
    });
  };

  const onEditAction = (data) => {
    onToggle();
    setEditData(data);
  };

  useEffect(() => {
    if (searchValue) {
      setPageNumber(1);
    }
  }, [searchValue]);

  console.log("count: ", pendingUsersData?.value?.onecharging?.length);

  return (
    <Stack
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        backgroundColor: theme.palette.bgForm.black1,
        color: "#fff",
        padding: isSmallScreen ? "20px" : "44px 94px 94px 94px",
      }}
    >
      <Stack>
        <Stack>
          <Stack width="100%" justifyContent="space-between" sx={{ flexDirection: isSmallScreen ? "column" : "row" }}>
            <Stack justifyItems="left">
              <Typography variant={isSmallScreen ? "h5" : "h4"}>User Accounts</Typography>
            </Stack>

            <Stack justifyItems="space-between" direction="row" marginTop={1}>
              <Button variant="contained" size={isSmallScreen ? "medium" : "large"} color="primary" startIcon={<AddOutlined />} onClick={onToggle}>
                Add
              </Button>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <Stack
        sx={{
          backgroundColor: theme.palette.bgForm.black3,
          borderRadius: "20px",
          marginTop: "20px",
          width: "100%",
        }}
      >
        <Stack direction="row" justifyContent="space-between">
          <Tabs value={status} onChange={onStatusChange} variant={isSmallScreen ? "scrollable" : "fullWidth"} scrollButtons="auto">
            <Tab
              value=""
              label="All"
              sx={{
                fontSize: "12px",
                fontWeight: 600,
              }}
            />

            <Tab
              value="true"
              label="Active"
              sx={{
                fontSize: "12px",
                fontWeight: 600,
              }}
            />

            <Tab
              value="false"
              label="Archived"
              sx={{
                fontSize: "12px",
                fontWeight: 600,
              }}
            />

            <Tab
              value="pending"
              label={
                <Box>
                  {pendingUsersData?.value?.onecharging?.length > 0 ? (
                    <Box display="flex" alignItems="center" gap={2}>
                      Pending
                      <Badge
                        badgeContent={pendingUsersData?.value?.onecharging?.length}
                        color="error"
                        sx={{
                          ".MuiBadge-badge": {
                            fontSize: "0.55rem",
                            fontWeight: 400,
                          },
                        }}
                      />
                    </Box>
                  ) : (
                    <>Pending</>
                  )}
                </Box>
              }
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                width: 120,
              }}
            />
          </Tabs>
        </Stack>

        <Divider variant="fullWidth" sx={{ background: "#2D3748", marginTop: "1px" }} />

        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ marginTop: "5px", padding: "10px" }}>
          <OutlinedInput
            placeholder="Search user"
            startAdornment={<Search sx={{ marginRight: 0.5, color: "#A0AEC0" }} />}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            sx={{
              flex: 1,
              borderRadius: "15px",
              fontSize: "small",
              fontWeight: 400,
              lineHeight: "1.4375rem",
              // backgroundColor: "#111927",
            }}
          />
        </Stack>

        {onPendingTab ? (
          <PendingAccountsTab
            data={pendingUsersData}
            isLoading={pendingUsersiIsLoading}
            isFetching={pendingUsersIsFetching}
            isSuccess={pendingUsersIsSuccess}
            isError={pendingUsersIsError}
            setPageNumber={setPageNumber}
            setPageSize={setPageSize}
            onPageNumberChange={onPageNumberChange}
            onPageSizeChange={onPageSizeChange}
            isSmallScreen={isSmallScreen}
            search={search}
          />
        ) : (
          <>
            {isSmallScreen ? (
              // Card-Based Layout for Small Screens
              <Stack spacing={2}>
                {isSuccess &&
                  !isLoading &&
                  !isFetching &&
                  data?.value?.users?.map((item, index) => (
                    <Card key={item.id} sx={{ backgroundColor: theme.palette.bgForm.black3, borderRadius: "15px", borderColor: "#2D3748" }}>
                      <CardContent>
                        <Stack spacing={1}>
                          <Stack direction="row" gap={0.5} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              LINE NO. {index + 1}
                            </Typography>

                            <Chip
                              label={item.is_Active ? "ACTIVE" : "INACTIVE"}
                              size="small"
                              sx={{
                                fontSize: "10px",
                                backgroundColor: item.is_Active ? "#112C32" : "#2D2823",
                                borderRadius: "none",
                                color: item.is_Active ? "#10B981" : "#D27D0E",
                              }}
                            />
                          </Stack>

                          <Typography variant="h6" sx={{ color: "#EDF2F7" }}>
                            {item.fullname}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            EMPLOYEE ID: {item.empId}
                          </Typography>

                          <Typography variant="body2" color="text.secondary">
                            USERNAME: {item.username}
                          </Typography>

                          <Typography variant="body2" sx={{ color: "#22B4BF" }}>
                            ROLE: {item.user_Role_Name}
                          </Typography>

                          <Stack direction="row" gap={0.5} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              ACCESS:
                            </Typography>

                            <UserAccountPermissions permissions={item.permission} />
                          </Stack>

                          <Stack direction="row" gap={0.5} alignItems="center">
                            <Typography variant="body2" color="text.secondary">
                              ACTION:
                            </Typography>

                            <UserAccountAction data={item} onReset={onResetPasswordAction} onArchive={onArchiveAction} onUpdate={onEditAction} />
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  ))}

                {isError && (
                  <Stack justifyContent="center" alignItems="center" padding={4}>
                    <img src={somethingWentWrong} alt="Something Went Wrong" className="something-went-wrong-table" />
                    <Typography variant="h6" color="error" align="center">
                      Something went wrong.
                    </Typography>
                  </Stack>
                )}

                {(isLoading || isFetching) && (
                  <Stack justifyContent="center" alignItems="center" padding={4}>
                    <CircularProgress />
                    <Typography variant="h5" color="#EDF2F7">
                      Please wait...
                    </Typography>
                  </Stack>
                )}

                {isSuccess && !data?.value?.users.length && !isLoading && !isFetching && (
                  <Stack justifyContent="center" alignItems="center" padding={4}>
                    <img src={noRecordsFound} alt="No Records Found" className="norecords-found-table" />
                    <Typography variant="h6" align="center">
                      No records found.
                    </Typography>
                  </Stack>
                )}

                <TablePagination
                  sx={{ color: "#A0AEC0", fontWeight: 400, background: "#1C2536", borderRadius: "0px 0px 20px 20px" }}
                  rowsPerPageOptions={[100, 300, 500, { value: data?.value?.totalCount, label: "All" }]}
                  component="div"
                  count={data?.value?.totalCount || 0}
                  rowsPerPage={data?.value?.pageSize || 100}
                  page={data?.value?.currentPage - 1 || 0}
                  onPageChange={onPageNumberChange}
                  onRowsPerPageChange={onPageSizeChange}
                />
              </Stack>
            ) : (
              // Table Layout for Larger Screens
              <>
                <Paper sx={{ width: "100%", overflow: "hidden" }}>
                  <TableContainer>
                    <Table stickyHeader sx={{ borderBottom: "none" }}>
                      <TableHead>
                        <TableRow>
                          {["LINE NO.", "FULLNAME", "USERNAME", "ROLE", "ACCOUNT ACCESS", "STATUS", "ACTIONS"].map((header, idx) => (
                            <TableCell
                              key={idx}
                              sx={{
                                background: "#1C2536",
                                color: "#D65DB1",
                                fontWeight: 700,
                                fontSize: "12px",
                              }}
                              align={["LINE NO.", "ACCOUNT ACCESS", "STATUS", "ACTIONS"].includes(header) ? "center" : "left"}
                            >
                              {header}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {isSuccess &&
                          !isLoading &&
                          !isFetching &&
                          data?.value?.users?.map((item, index) => (
                            <TableRow key={item.id}>
                              <TableCell
                                sx={{
                                  color: "#EDF2F7",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                }}
                                align="center"
                              >
                                {index + 1}
                              </TableCell>

                              <TableCell>
                                <Typography
                                  sx={{
                                    color: "#EDF2F7",
                                    fontSize: "14px",
                                    fontWeight: 500,
                                  }}
                                >
                                  {item.fullname}
                                </Typography>
                                <Typography
                                  sx={{
                                    color: theme.palette.text.secondary,
                                    fontSize: "12px",
                                    fontWeight: 500,
                                  }}
                                >
                                  {item.empId}
                                </Typography>
                              </TableCell>

                              <TableCell
                                sx={{
                                  color: "#EDF2F7",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                }}
                              >
                                {item.username}
                              </TableCell>

                              <TableCell
                                sx={{
                                  color: "#22B4BF",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                }}
                              >
                                {item.user_Role_Name}
                              </TableCell>

                              <TableCell
                                sx={{
                                  color: "#EDF2F7",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                }}
                                align="center"
                              >
                                <UserAccountPermissions permissions={item.permission} />
                              </TableCell>

                              <TableCell
                                sx={{
                                  color: "#EDF2F7",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                }}
                                align="center"
                              >
                                <Chip
                                  variant="filled"
                                  size="30px"
                                  sx={{
                                    fontSize: "13px",
                                    backgroundColor: item.is_Active ? "#112C32" : "#2D2823",
                                    color: item.is_Active ? "#10B981" : "#D27D0E",
                                    fontWeight: 800,
                                  }}
                                  label={item.is_Active ? "ACTIVE" : "INACTIVE"}
                                />
                              </TableCell>

                              <TableCell
                                sx={{
                                  color: "#EDF2F7",
                                  fontSize: "14px",
                                  fontWeight: 500,
                                }}
                                align="center"
                              >
                                <UserAccountAction data={item} onReset={onResetPasswordAction} onArchive={onArchiveAction} onUpdate={onEditAction} />
                              </TableCell>
                            </TableRow>
                          ))}

                        {isError && (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <img src={somethingWentWrong} alt="Something Went Wrong" className="something-went-wrong-table" />
                              <Typography variant="h5" color="#EDF2F7" marginLeft={2}>
                                Something went wrong.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {(isLoading || isFetching) && (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <CircularProgress />
                              <Typography variant="h5" color="#EDF2F7">
                                Please wait...
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}

                        {isSuccess && !data?.value?.users.length && !isLoading && !isFetching && (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              <img src={noRecordsFound} alt="No Records Found" className="norecords-found-table" />
                              <Typography variant="h5" color="#EDF2F7" marginLeft={2}>
                                No records found.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>

                <TablePagination
                  sx={{ color: "#A0AEC0", fontWeight: 400, background: "#1C2536", borderRadius: "0px 0px 20px 20px" }}
                  rowsPerPageOptions={[100, 300, 500, { value: data?.value?.totalCount, label: "All" }]}
                  component="div"
                  count={data?.value?.totalCount || 0}
                  rowsPerPage={data?.value?.pageSize || 100}
                  page={data?.value?.currentPage - 1 || 0}
                  onPageChange={onPageNumberChange}
                  onRowsPerPageChange={onPageSizeChange}
                />
              </>
            )}
          </>
        )}

        <UserAccountDialog open={open} onClose={onDialogClose} data={editData} />
      </Stack>
    </Stack>
  );
};

export default UserAccounts;
